/**
 * "Manage your appointment anywhere" app-download banner. A blue full-bleed section with
 * a phone-collage graphic and a heading / subtitle / "Get the App" CTA. Matches the Figma
 * designs for desktop (node 1491:4660) and mobile (node 1491:3877): graphic-left / text-right
 * on desktop, flipping to text-top / phone-below on mobile.
 */
/* eslint-disable @next/next/no-img-element */
import React from 'react'
import { getImageProps } from 'next/image'
import styled from '@emotion/styled'
import { gsColors as c } from '../../styles/Variables'
import { mq } from '../../helpers'

const APP_STORE_URL = 'https://apps.apple.com/us/app/glamsquad/id766676007'
const GRAPHIC_ALT = 'The GLAMSQUAD app showing an upcoming appointment and pro details'

/**
 * AppDownload component — call-to-action banner for downloading the GLAMSQUAD mobile app.
 *
 * The phone-collage graphic is art-directed: a different asset per breakpoint, served
 * through a single <picture> so the browser downloads only the matching one (two
 * CSS-toggled <img>s downloaded both). getImageProps routes both through /_next/image
 * for AVIF/WebP; loading="lazy" keeps this below-the-fold banner off the critical path.
 * @returns {React.ReactElement}
 */
const AppDownload = () => {
  const { props: { srcSet: mobileSrcSet } } = getImageProps({
    src: '/service-detail/app-promo-mobile.png', alt: GRAPHIC_ALT, width: 334, height: 270,
  })
  const { props: desktopImgProps } = getImageProps({
    src: '/service-detail/app-promo-desktop.png', alt: GRAPHIC_ALT, width: 801, height: 322, loading: 'lazy',
  })

  return (
    <Wrapper>
      <div className="banner">
        <div className="graphic">
          <picture>
            <source media="(max-width: 900px)" srcSet={mobileSrcSet} />
            <img {...desktopImgProps} />
          </picture>
        </div>

        <div className="content">
          <div className="text">
            <h2>Manage your appointment anywhere, anytime.</h2>
            <p>Download the free Glamsquad App</p>
          </div>
          <a className="cta" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
            Get the App
          </a>
        </div>
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.section`
  width: 100%;
  max-width: 1600px;
  padding: 64px 80px;

  ${mq('max', '900px')} {
    padding: 40px 24px;
  }

  .banner {
    position: relative;
    overflow: hidden;
    background: ${c.cobalt};
    display: grid;
    grid-template-columns: minmax(0, 1.35fr) minmax(0, 1fr);
    align-items: center;
    min-height: 322px;

    ${mq('max', '900px')} {
      grid-template-columns: minmax(0, 1fr);
      min-height: 0;
    }
  }

  .graphic {
    align-self: stretch;
    ${mq('max', '900px')} {
      order: 2;
      justify-self: center;
      width: 100%;
      max-width: 360px;
    }
  }

  .graphic picture {
    display: block;
    height: 100%;
  }

  .graphic img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: left bottom;

    ${mq('max', '900px')} {
      height: auto;
      object-fit: unset;
      object-position: unset;
    }
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 48px 64px 48px 48px;

    ${mq('max', '900px')} {
      order: 1;
      padding: 28px 24px 0;
      gap: 16px;
    }
  }

  .text {
    display: flex;
    flex-direction: column;
    gap: 16px;
    ${mq('max', '900px')} {
      gap: 8px;
    }
  }

  h2 {
    font-family: Garnett;
    font-size: 28px;
    font-weight: 600;
    line-height: 1.38;
    text-transform: capitalize;
    color: ${c.white};
    margin: 0;
    ${mq('max', '900px')} {
      font-size: 20px;
    }
  }

  p {
    font-family: Euclid;
    font-size: 20px;
    line-height: 24px;
    color: ${c.white};
    margin: 0;
    ${mq('max', '900px')} {
      font-size: 14px;
    }
  }

  .cta {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 200px;
    height: 56px;
    background: ${c.salmon};
    color: ${c.cobalt};
    font-family: Euclid;
    font-size: 18px;
    font-weight: 600;
    letter-spacing: 1.44px;
    text-transform: capitalize;
    text-decoration: none;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.9;
    }

    ${mq('max', '900px')} {
      width: 100%;
      height: 48px;
      font-size: 14px;
      letter-spacing: 1.12px;
    }
  }
`

export default AppDownload
